import React, { useState, useEffect, useRef } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

interface BeamIDsManualInputDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (beamIds: string[]) => void;
  propertyId: string;
  existingBeamIds?: string[];
}

/**
 * BeamIDsManualInputDialog Component
 *
 * Dialog for manually entering beam IDs to assign to a property
 * Expected format: Comma-separated beam IDs or ranges
 * Examples: "1, 2, 3, 5" or "1-5, 8, 10-12"
 *
 * Follows Theme_Format_readme.md standards:
 * - Draggable blue header
 * - Centered on open
 * - Modal with backdrop
 */
export const BeamIDsManualInputDialog: React.FC<
  BeamIDsManualInputDialogProps
> = ({ isOpen, onClose, onImport, propertyId, existingBeamIds = [] }) => {
  const [inputValue, setInputValue] = useState<string>(
    existingBeamIds.join(", ")
  );
  const [error, setError] = useState<string | null>(null);

  // Draggable state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(existingBeamIds.join(", "));
  }, [existingBeamIds]);

  // Center dialog on open
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      const dialogWidth = 500;
      const dialogHeight = dialogRef.current.offsetHeight || 500;
      setPosition({
        x: Math.max(0, (window.innerWidth - dialogWidth) / 2),
        y: Math.max(0, (window.innerHeight - dialogHeight) / 2),
      });
    }
  }, [isOpen]);

  // Dragging handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest(".close-button")) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
    document.body.style.userSelect = "none";
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const dialogHeight = dialogRef.current?.offsetHeight || 500;
      const newX = Math.max(
        0,
        Math.min(e.clientX - dragStart.x, window.innerWidth - 500)
      );
      const newY = Math.max(
        0,
        Math.min(e.clientY - dragStart.y, window.innerHeight - dialogHeight)
      );
      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.body.style.userSelect = "";
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragStart]);

  /**
   * Parse beam IDs from comma-separated input
   * Supports both individual IDs (1, 2, 3) and ranges (1-5)
   */
  const parseBeamIds = (text: string): string[] => {
    const beamIds: string[] = [];
    const parts = text
      .split(",")
      .map((p) => p.trim())
      .filter((p) => p);

    for (const part of parts) {
      // Check if it's a range (e.g., "1-5")
      if (part.includes("-")) {
        const [start, end] = part.split("-").map((s) => parseInt(s.trim()));
        if (isNaN(start) || isNaN(end) || start > end) {
          throw new Error(`Invalid range: ${part}`);
        }
        for (let i = start; i <= end; i++) {
          beamIds.push(i.toString());
        }
      } else {
        // Single beam ID
        const id = parseInt(part);
        if (isNaN(id)) {
          throw new Error(`Invalid beam ID: ${part}`);
        }
        beamIds.push(id.toString());
      }
    }

    return beamIds;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    setError(null);
  };

  const handleImportClick = () => {
    if (!inputValue.trim()) {
      setError("Please enter beam IDs");
      return;
    }

    try {
      const parsedIds = parseBeamIds(inputValue);

      if (parsedIds.length === 0) {
        setError("No valid beam IDs found");
        return;
      }

      onImport(parsedIds);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid input format");
    }
  };

  const handleClose = () => {
    setInputValue(existingBeamIds.join(", "));
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-30 z-50"
        onClick={handleClose}
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        className="fixed bg-white dark:bg-gray-800 rounded-lg shadow-xl w-[500px] z-50"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
      >
        {/* Draggable Header */}
        <div
          className="bg-blue-600 text-white px-4 py-3 rounded-t-lg cursor-grab active:cursor-grabbing select-none flex justify-between items-center"
          onMouseDown={handleMouseDown}
        >
          <h2 className="text-lg font-semibold">Enter Beam IDs</h2>
          <button
            className="close-button text-white hover:text-gray-200 p-1 rounded transition-colors"
            onClick={handleClose}
            type="button"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
              Property ID:{" "}
              <span className="font-semibold text-blue-600">{propertyId}</span>
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Enter beam IDs separated by commas. You can also use ranges:
            </p>
            <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded text-xs font-mono space-y-1">
              <div>
                <span className="text-gray-600 dark:text-gray-400">
                  Individual:
                </span>{" "}
                1, 2, 3, 5
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">
                  Ranges:
                </span>{" "}
                1-5, 8, 10-12
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Mixed:</span>{" "}
                1, 3-7, 10, 15-20
              </div>
            </div>
          </div>

          {/* Text Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Beam IDs
            </label>
            <textarea
              value={inputValue}
              onChange={handleInputChange}
              placeholder="e.g., 1, 2, 3-5, 8, 10-15"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={4}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Preview */}
          {inputValue && !error && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Preview:
              </p>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  {inputValue}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleImportClick}
            disabled={!inputValue.trim()}
            className={`px-4 py-2 text-sm font-medium text-white rounded transition-colors ${
              inputValue.trim()
                ? "bg-blue-600 hover:bg-blue-700"
                : "bg-gray-400 cursor-not-allowed"
            }`}
          >
            Apply to Beams
          </button>
        </div>
      </div>
    </>
  );
};
